import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Box,
  Card,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import {
  useCreateUserRoleMutation,
  useGetAllUserPrivilegesQuery,
} from "../../../Api/attoDeskApi";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { IPrivilege, Privilege } from "../../../Api/Interface/api.interface";
import { toProperCase } from "../../../Components/Validation/toProperCase";

interface FormValues {
  roleName: string;
  privileges: number[];
}

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [newCompany, { isLoading }] = useCreateUserRoleMutation();
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);

  const { data: privileges, isLoading: privilegesLoading } =
    useGetAllUserPrivilegesQuery();

  const privilegesList = useMemo(() => {
    return privileges?.data as IPrivilege[];
  }, [privileges?.data]);

  const formik = useFormik<FormValues>({
    initialValues: {
      roleName: "",
      privileges: [],
    },

    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = {
          roleName: toProperCase(values.roleName),
          privileges: selectedCategories,
        };

        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          resetForm();
          setSelectedCategories([]);
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const cancelFunction = () => {
    formik.resetForm();
    setSelectedCategories([]);
  };

  const formValid = useMemo(() => {
    return (
      formik.values.roleName !== "" && formik.values.roleName !== undefined
    );
  }, [formik.values.roleName]);

  const handleCategoryChange = (categoryId: number) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      setSelectedCategories(privilegesList.map((category) => category.id));
    } else {
      setSelectedCategories([]);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item lg={3} md={3} sm={12} xs={12}>
        <Card className={classes.stepperCard}>
          <Grid
            container
            justifyContent={"space-between"}
            spacing={5}
            sx={{ borderBottom: 2, padding: 2 }}
            className={classes.titleGrid}
          >
            <Grid
              item
              lg={12}
              md={12}
              sm={12}
              xs={12}
              pt={2}
              alignContent={"center"}
            >
              <Typography className={classes.cardTitle}>Create</Typography>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item lg={9} md={9} sm={12} xs={12}>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        User Role Information
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            User Role{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="User Role"
                            size="small"
                            {...formik.getFieldProps("roleName")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Privileges
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <FormControl sx={{ width: "100%" }}>
                            <Select
                              MenuProps={MenuProps}
                              input={
                                <OutlinedInput
                                  sx={{ height: `${theme.spacing(5)}` }}
                                />
                              }
                              multiple
                              value={formik.values.privileges}
                              onChange={(event) => {
                                const { value } = event.target;
                                // Update formik state
                                formik.setFieldValue(
                                  "privileges",
                                  typeof value === "string"
                                    ? value.split(",")
                                    : value
                                );
                              }}
                              renderValue={(selected) => {
                                if (selected.length === 0) {
                                  return <em>Placeholder</em>;
                                }

                                const selectedNames = privileges?.data
                                  ?.filter((priv: Privilege) =>
                                    selected.includes(priv.id)
                                  )
                                  .map((priv: Privilege) => priv.name);

                                return selectedNames?.join(", ");
                              }}
                            >
                              {privileges?.data?.map((priv: Privilege) => (
                                <MenuItem key={priv.id} value={priv.id}>
                                  <Checkbox
                                    checked={formik.values.privileges.includes(
                                      priv.id
                                    )}
                                  />
                                  <ListItemText primary={priv.name} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid> */}
                  </Grid>
                </Card>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Select Privileges
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* <Grid container spacing={3} p={"24px"}>
                    <Grid container spacing={3} p={"24px"}>
                      {privilegesLoading ? (
                        <>
                          <Grid
                            item
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              minHeight: "200px",
                            }}
                          >
                            <CircularProgress />
                          </Grid>
                        </>
                      ) : (
                        privilegesList.map((category: IPrivilege) => (
                          <Grid
                            item
                            lg={4}
                            md={4}
                            sm={12}
                            xs={12}
                            key={category.id}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedCategories.includes(
                                    category.id
                                  )}
                                  onChange={() =>
                                    handleCategoryChange(category.id)
                                  }
                                />
                              }
                              // label={category.display_name}
                              label={
                                category.display_name
                                  ? category.display_name
                                  : category.name
                              }
                            />
                          </Grid>
                        ))
                      )}
                    </Grid>
                  </Grid> */}

                  <Grid container spacing={3} p={"24px"}>
                    <Grid container spacing={3} p={"24px"}>
                      {privilegesLoading ? (
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "200px",
                          }}
                        >
                          <CircularProgress />
                        </Grid>
                      ) : (
                        <>
                          {/* Select All Checkbox */}
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    selectedCategories.length ===
                                    privilegesList.length
                                  }
                                  onChange={handleSelectAllChange}
                                />
                              }
                              label="Select All"
                            />
                          </Grid>

                          {/* Individual Privileges */}
                          {privilegesList.map((category: IPrivilege) => (
                            <Grid
                              item
                              lg={4}
                              md={4}
                              sm={12}
                              xs={12}
                              key={category.id}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedCategories.includes(
                                      category.id
                                    )}
                                    onChange={() =>
                                      handleCategoryChange(category.id)
                                    }
                                  />
                                }
                                label={
                                  category.display_name
                                    ? category.display_name
                                    : category.name
                                }
                              />
                            </Grid>
                          ))}
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid container spacing={3} p={"24px"}>
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        className={classes.addButton}
                        onClick={cancelFunction}
                      >
                        Reset
                      </Button>
                      <Box m={0.5}></Box>

                      <Button
                        variant="contained"
                        startIcon={
                          isLoading ? (
                            <CircularProgress
                              size={18}
                              className={classes.isLoading}
                            />
                          ) : (
                            <SaveAltIcon />
                          )
                        }
                        className={classes.addButton}
                        onClick={() => formik.handleSubmit()}
                        disabled={!formValid || isLoading}
                      >
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </form>
        </FormikProvider>
      </Grid>
    </Grid>
  );
};

export default Index;
